import { Injectable, NgZone, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { WithoutInterceptor } from './without.interceptor';

/** One overdue rental line for the toolbar bell panel. */
export interface RentalOverdueRow {
    detailId: number;
    invoiceId: number;
    invoiceNo: string;
    itemName: string;
    quantity: number;
    devoteeName: string;
    phone: string;
    returnDueDate: number;
    daysOverdue: number;
    depositAmount: string;
}

/**
 * Polls `v2/rental/overdue/summary` for the toolbar rental bell (WS-R; the
 * kdc_registry notification-bell pattern on the booking-queue-indicator
 * plumbing). Overdue = a RENTED line past its return date + the temple's
 * grace days — computed server-side, nothing stored; a row leaves the feed
 * the moment the item is returned. WithoutInterceptor so the background poll
 * never triggers the global loader; the timer runs outside the Angular zone.
 */
@Injectable({ providedIn: 'root' })
export class RentalOverdueStatusService {

    readonly count = signal<number>(0);
    readonly unreachable = signal(false);

    private timerId: any = null;
    private consumers = 0;
    // R52: pause polling while the window is hidden — same pattern as
    // BookingQueueStatusService (the two pollers stay in lockstep).
    private visibilityListener: (() => void) | null = null;

    constructor(private http: WithoutInterceptor, private zone: NgZone) { }

    /** Reference-counted so multiple widgets share one poll loop. */
    startPolling(intervalMs = 60000) {
        this.consumers++;
        if (this.timerId !== null) {
            return;
        }
        this.refresh();
        this.zone.runOutsideAngular(() => {
            this.timerId = setInterval(() => this.refresh(), intervalMs);
            // R52: catch-up refresh the moment the window is shown again.
            this.visibilityListener = () => {
                if (!document.hidden) {
                    this.refresh();
                }
            };
            document.addEventListener('visibilitychange', this.visibilityListener);
        });
    }

    stopPolling() {
        this.consumers = Math.max(0, this.consumers - 1);
        if (this.consumers === 0 && this.timerId !== null) {
            clearInterval(this.timerId);
            this.timerId = null;
            if (this.visibilityListener) {
                document.removeEventListener('visibilitychange', this.visibilityListener);
                this.visibilityListener = null;
            }
        }
    }

    refresh() {
        // R52: no request while the document is hidden; the visibilitychange
        // listener fires a catch-up refresh on return.
        if (document.hidden) {
            return;
        }
        if (!this.hasSession()) {
            return;
        }
        this.http.get<any>(environment.apiUrl + 'v2/rental/overdue/summary').subscribe({
            next: (resp) => this.zone.run(() => {
                this.count.set(+resp?.result?.count || 0);
                this.unreachable.set(!resp?.result);
            }),
            error: () => this.zone.run(() => this.unreachable.set(true))
        });
    }

    rows(): Observable<any> {
        return this.http.get<any>(environment.apiUrl + 'v2/rental/overdue');
    }

    private hasSession(): boolean {
        try {
            const user = sessionStorage.getItem('CURRENT_USER');
            return !!user && !!JSON.parse(user)?.accessToken;
        } catch {
            return false;
        }
    }
}
